import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from 'react-query';
import { useTranslation } from 'hooks';
import {
  useTransportRouteSearchParams,
  type Filters,
} from 'hooks/store/use-transport-route-search-params';
import {
  useCityOptions,
  useUfOptions,
  useTransporRouteStatusOptions,
  useCarrierOptions,
} from 'hooks/api/options';
import { Form, FormField } from 'components/ui/form';
import { ComboboxGs, type SelectContentProps } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';

import { formSchema, type FormSchema } from './form-schema';
import { translations } from './translations';

type Props = {
  onSubmitFilters: (filters: Filters) => void;
  onResetFilters: () => void;
};

export const TransportRouteFilters = ({
                                        onSubmitFilters,
                                        onResetFilters,
                                      }: Props) => {
  const { getTranslation: t } = useTranslation(translations);
  const { filters } = useTransportRouteSearchParams({ persisted: true });
  const [formHydrated, setFormHydrated] = React.useState(false);
  const carrierOptions = useCarrierOptions();
  const statusOptions = useTransporRouteStatusOptions();
  const ufOptions = useUfOptions();
  const { fetchCities } = useCityOptions();

  const [originCityOptions, setOriginCityOptions]
    = React.useState<SelectContentProps[]>([]);
  const [destinationCityOptions, setDestinationCityOptions]
    = React.useState<SelectContentProps[]>([]);

  const originCityOptionsMutation = useMutation({
    mutationFn: fetchCities,
    onSuccess: (data) => {
      setOriginCityOptions(data.singleSelectOptions);
    },
  });

  const destinationCityOptionsMutation = useMutation({
    mutationFn: fetchCities,
    onSuccess: (data) => {
      setDestinationCityOptions(data.singleSelectOptions);
    },
  });

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const onOriginUfChange = React.useCallback((uf: string) => {
    form.resetField('origin_ibge');
    originCityOptionsMutation.mutate(uf);
  }, [form]);

  const onOriginUfClear = React.useCallback(() => {
    form.resetField('origin_ibge');
    setOriginCityOptions([]);
  }, [form]);

  const onDestinationUfChange = React.useCallback((uf: string) => {
    form.resetField('destination_ibge');
    destinationCityOptionsMutation.mutate(uf);
  }, [form]);

  const onDestinationUfClear = React.useCallback(() => {
    form.resetField('destination_ibge');
    setDestinationCityOptions([]);
  }, [form]);

  const hydrateForm = React.useCallback(() => {
    const {
      carrier_id, status,
      origin_uf, origin_ibge,
      destination_uf, destination_ibge
    } = filters;

    carrier_id && form.setValue('carrier_id', carrier_id);
    status && form.setValue('status', status);

    origin_uf && form.setValue('origin_uf', origin_uf);
    origin_uf && onOriginUfChange(origin_uf);
    origin_ibge && form.setValue('origin_ibge', origin_ibge);

    destination_uf && form.setValue('destination_uf', destination_uf);
    destination_uf && onDestinationUfChange(destination_uf);
    destination_ibge && form.setValue('destination_ibge', destination_ibge);
    setFormHydrated(true);
  }, [
    filters,
    form,
    onOriginUfChange,
    onDestinationUfChange,
  ]);

  const resetForm = React.useCallback(() => {
    form.reset({
      carrier_id: undefined,
      origin_ibge: undefined,
      origin_uf: undefined,
      destination_ibge: undefined,
      destination_uf: undefined,
      status: undefined,
    }, { keepValues: false });
    onResetFilters();
  }, [form, onResetFilters]);

  const onFormSubmit = React.useCallback((formData: FormSchema) => {
    onSubmitFilters(formData);
  }, [onSubmitFilters]);

  React.useEffect(() => {
    !formHydrated && hydrateForm();
  }, [formHydrated, hydrateForm]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onFormSubmit)} className={'w-full'}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <FormField
            control={form.control}
            name={'origin_uf'}
            render={({ field }) => (
              <ComboboxGs
                label={t('originUf')}
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={ufOptions.singleSelectOptions}
                onChange={onOriginUfChange}
                onClear={onOriginUfClear}
                clearable
              />
            )}
          />
          <FormField
            control={form.control}
            name={'origin_ibge'}
            render={({ field }) => (
              <ComboboxGs
                label={t('originCity')}
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={originCityOptions}
                clearable
              />
            )}
          />
          <FormField
            control={form.control}
            name={'destination_uf'}
            render={({ field }) => (
              <ComboboxGs
                label={t('destinationUf')}
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={ufOptions.singleSelectOptions}
                onChange={onDestinationUfChange}
                onClear={onDestinationUfClear}
                clearable
              />
            )}
          />
          <FormField
            control={form.control}
            name={'destination_ibge'}
            render={({ field }) => (
              <ComboboxGs
                label={t('destinationCity')}
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={destinationCityOptions}
                clearable
              />
            )}
          />
          <FormField
            control={form.control}
            name={'carrier_id'}
            render={({ field }) => (
              <ComboboxGs
                label={t('carrier')}
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={carrierOptions.singleSelectOptions}
                clearable
              />
            )}
          />
          <FormField
            control={form.control}
            name={'status'}
            render={({ field }) => (
              <ComboboxGs
                label={t('status')}
                form={form}
                fieldName={field.name}
                fieldValue={field.value}
                content={statusOptions.singleSelectOptions}
                clearable
              />
            )}
          />
        </div>
        <div className={'flex gap-4 mt-6'}>
          <Button.Root
            type={'button'}
            variant={'secondary'}
            size={'sm'}
            onClick={resetForm}
          >
            {t('resetButton')}
          </Button.Root>
          <Button.Root type={'submit'} size={'sm'}>
            {t('submitButton')}
          </Button.Root>
        </div>
      </form>
    </Form>
  );
};